import * as React from "react";
import { cn } from "@/lib/utils";
import {
  blockInvalidNumericInput,
  handleNumericPaste,
} from "@/utils/validation/helpers";
import { DollarSign, X } from "lucide-react";
import { type VariantProps } from "class-variance-authority";
import { inputVariants } from "./input";


/** Generic numeric text-input that can show a unit on either side */
export interface NumberInputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  unit?: string;
  /** `"right"` (default) or `"left"` */
  unitPosition?: "right" | "left";
  /** Expand the interactive hit area to 44x44 via a pseudo-element overlay
   *  without inflating the visible input footprint. Ignored when the input
   *  is rendered in full-width mode. */
  touchTarget?: boolean;
  /** Number of decimal places for currency formatting. Defaults to 2 for `$` unit. Set to 0 for whole numbers. */
  decimals?: number;
  /** Optional soft bounds to guard realtime onChange propagation */
  minValue?: number;
  maxValue?: number;
}

const UnitDisplay = ({ unit, className }: { unit: string; className?: string }) => {
  const iconClasses = "h-4 w-4 text-foreground/75 flex-shrink-0";
  if (unit.toLowerCase() === 'x') {
    return <X className={cn(iconClasses, className)} />;
  }
  if (unit === '$') {
    return <DollarSign className={cn(iconClasses, className)} />;
  }
  return (
    <span className={cn("text-sm text-foreground/80 select-none flex-shrink-0", className)}>
      {unit}
    </span>
  );
};

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ unit, unitPosition = "right", touchTarget = true, decimals, className, variant, size = "xs", radius, state, leadingIcon, trailingIcon, minValue, maxValue, ...props }, ref) => {
    const { value, onBlur, onChange, onKeyDown, id: idProp, name: nameProp, ...restProps } = props;

    // Auto-generate an id/name when the caller hasn't provided one so browser
    // form semantics (autofill, document.forms, speech-to-click targeting)
    // have something to key off of. Callers can still override by passing
    // explicit `id`/`name` props. Fixes the Chrome DevTools "form field
    // element should have an id or name attribute" warning raised by every
    // NumberInput across the config forms.
    const generatedId = React.useId();
    const resolvedId = idProp ?? `number-input-${generatedId}`;
    const resolvedName = nameProp ?? resolvedId;

    // Detect if this input should fill its container width
    const isFullWidth = className?.includes('w-full') ?? false;
    // `touchTarget` uses a pseudo-element overlay to provide a 44x44 hit area
    // without inflating the visible input (see `.touch-target-pseudo` in index.css).
    const useTouchTargetPseudo = touchTarget && !isFullWidth;

    // Local state to handle partial/string inputs during user typing
    const currencyDecimals = decimals ?? (unit === '$' ? 2 : undefined);

    /** Format a number with the configured decimals, but strip trailing `.00` for whole numbers */
    const formatValue = React.useCallback((num: number): string => {
      if (currencyDecimals === undefined) return String(num);
      const fixed = num.toFixed(currencyDecimals);
      // Only show decimals if there's a non-zero fractional part
      if (currencyDecimals > 0 && num % 1 === 0) return String(Math.round(num));
      return fixed;
    }, [currencyDecimals]);

    const [localValue, setLocalValue] = React.useState(() => {
      const raw = value ?? '';
      const num = Number(raw);
      if (currencyDecimals !== undefined && raw !== '' && !isNaN(num)) return formatValue(num);
      return String(raw);
    });

    // Calculate dynamic width based on content (only used in shrink-wrap mode)
    const calculateInputWidth = React.useCallback((val: string) => {
      // Minimum width — wide enough to comfortably show a few digits with padding
      const minInputWidth = 5;
      // Calculate width based on content length, with a reasonable maximum
      const contentWidth = Math.max(minInputWidth, val.length + 1 || minInputWidth);
      const maxInputWidth = 10;
      return Math.min(contentWidth, maxInputWidth);
    }, []);

    const [inputWidth, setInputWidth] = React.useState(() => calculateInputWidth(String(value ?? '')));

    // Sync with external value prop changes (e.g., from form resets)
    const localValueRef = React.useRef(localValue);
    localValueRef.current = localValue;

    React.useEffect(() => {
      // Only update local state if the numeric value differs from the prop.
      // This prevents overwriting user input like "12." with "12".
      const numericProp = value === null || value === undefined || value === '' ? NaN : Number(value);
      const numericLocal = parseFloat(localValueRef.current ?? '');

      if (numericProp !== numericLocal && !(isNaN(numericProp) && isNaN(numericLocal))) {
        const next = currencyDecimals !== undefined && !isNaN(numericProp) ? formatValue(numericProp) : String(value ?? '');
        setLocalValue(next);
        setInputWidth(calculateInputWidth(next));
      }
    }, [value, calculateInputWidth, currencyDecimals, formatValue]);

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      let raw = e.currentTarget.value.trim();
      const numValue = parseFloat(raw);
      
      if (!isNaN(numValue)) {
        // Clamp within provided bounds on blur
        let clamped = numValue;
        if (typeof minValue === 'number') clamped = Math.max(minValue, clamped);
        if (typeof maxValue === 'number') clamped = Math.min(maxValue, clamped);
        if (currencyDecimals !== undefined) {
          raw = formatValue(clamped);
        } else {
          // For non-currency, round to 2 decimal places but strip trailing zeros.
          raw = String(Number(clamped.toFixed(2)));
        }
      } else {
        raw = ""; // Clear if not a valid number
      }

      // Reflect sanitized value in local UI too
      setLocalValue(raw);
      setInputWidth(calculateInputWidth(raw));
      
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: raw },
      };
      
      // Propagate the cleaned, parsed value up to the parent form logic
      onChange?.(syntheticEvent);
      
      // Call the original onBlur from props (used for validation)
      onBlur?.(e);
    };
    
    // Handle text alignment. If a text-alignment class is passed, it should apply to the input.
    const textAlignment = className?.match(/text-(left|center|right|justify)/)?.[0];
    const finalAlignment = textAlignment || (unitPosition === "right" ? "text-right" : "text-center");

    // Use the input variants but modify for wrapper container
    const baseInputClasses = inputVariants({ variant, size, radius, state, leadingIcon, trailingIcon });
    
    // Extract classes that should apply to the wrapper vs the inner input
    const wrapperClasses = "inline-flex items-center gap-1 focus-within:outline-none";
    const inputClasses = "number-input-inner px-0 py-1 border-none shadow-none appearance-none bg-transparent text-foreground outline-none focus:outline-none focus-visible:outline-none focus:ring-0 disabled:cursor-not-allowed placeholder:text-muted-foreground/40 tabular-nums font-mono";

    return (
      <div
        className={cn(
          // Remove width and flex properties from variants to allow shrink-wrapping
          // (unless full-width mode is requested)
          isFullWidth
            ? baseInputClasses
            : cn(baseInputClasses.replace(/\bw-full\b/g, '').replace(/\bflex\b/g, ''), 'input-recessed'),
          wrapperClasses,
          props.disabled && "cursor-not-allowed opacity-50",
          useTouchTargetPseudo && "touch-target-pseudo",
          // In shrink-wrap mode, align to end
          !isFullWidth && finalAlignment === "text-right" && "justify-end",
          !isFullWidth && unit === "$" && "ml-auto",
          // We filter out text-alignment and w-full from the className passed to the wrapper,
          // as text-alignment will be applied to the inner input, and w-full is handled above.
          className?.replace(/text-(left|center|right|justify)/g, '').replace(/w-full/g, '').trim()
        )}
        onPointerDown={useTouchTargetPseudo && !props.disabled ? (e) => {
          // When the touch-target-pseudo overlay intercepts pointer events
          // (e.g., a tap in the expanded hit-area outside the visible input),
          // forward focus to the inner input so the field remains editable.
          if (e.target === e.currentTarget) {
            const input = e.currentTarget.querySelector('input');
            if (input) {
              e.preventDefault();
              input.focus();
            }
          }
        } : undefined}
      >
        {unit && unitPosition === "left" && <UnitDisplay unit={unit} />}

        <input
          ref={ref}
          id={resolvedId}
          name={resolvedName}
          type="text"
          inputMode="decimal"
          onKeyDown={(e) => {
            blockInvalidNumericInput(e, { allowDecimal: true });
            onKeyDown?.(e);
          }}
          onPaste={(e) => {
            handleNumericPaste(e, { allowDecimal: true });
          }}
          style={isFullWidth ? undefined : {
            width: `${inputWidth}ch`,
            minWidth: '5ch',
            maxWidth: '10ch',
          }}
          className={cn(
            inputClasses,
            "font-mono",
            isFullWidth ? "flex-1 min-w-0" : "",
            finalAlignment
          )}
          {...restProps}
          value={localValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalValue(newValue);
            setInputWidth(calculateInputWidth(newValue));
            // Only propagate to parent if within soft bounds (or empty)
            const n = parseFloat(newValue);
            const isEmpty = newValue.trim() === '';
            const isNumeric = !Number.isNaN(n);
            const withinMin = typeof minValue !== 'number' || n >= minValue;
            const withinMax = typeof maxValue !== 'number' || n <= maxValue;
            if (isEmpty || (isNumeric && withinMin && withinMax)) {
              const syntheticEvent = {
                ...e,
                target: { ...e.target, value: newValue },
              };
              onChange?.(syntheticEvent);
            }
          }}
          onBlur={handleBlur}
        />

        {unit && unitPosition === "right" && <UnitDisplay unit={unit} />}
      </div>
    );
  }
);
NumberInput.displayName = "NumberInput";

export { NumberInput };
